// This line's voicemail — the whole of it, on the line.
//
// WHERE IT WAS. Behind a link on the customer page reading "Show account-wide numbers,
// routing, extensions and messaging" — which does not mention voicemail at all — and then
// scoped to the CUSTOMER, listing every mailbox they own with nothing saying which belonged
// to which number. Kevin, 2026-08-13: "the voicemail is per line."
//
// WHICH MAILBOX IS "THE LINE'S". FreePBX gives a mailbox to an extension; there is no such
// thing as a group mailbox. So a number that rings five phones takes its messages on ONE of
// them — the primary extension's — because that is where the ring group's no-answer
// destination points (`voicemailDestinationFor`, Kevin 2026-08-12). That is stated on the
// card rather than left for the operator to infer from five identical boxes.
//
// ONLY THAT ONE IS SHOWN AS A MAILBOX. Kevin, 2026-08-13: "when the call is routed to a ring
// group, only one mailbox should be used (that of the primary extension) for the ring group's
// Destination if no answer." The build already does exactly that — it enables voicemail on
// the primary alone — so drawing a full mailbox card per ringing extension showed five
// mailboxes where one exists. Only the primary still gets a MAILBOX CARD, for that reason.
//
// AMENDED 2026-08-27, because that ruling was about a line ringing five phones and a menu is a
// different case: keys 2, 3 and 4 of Lyons Communications' attendant are Sales, Technical
// Support and Customer Service, and each of those prompts ends "to leave a voicemail, press 1".
// So the other extensions are now a row each with a switch, rather than a paragraph calling any
// non-primary mailbox drift that "goes unheard". The switch is also the only way to turn one ON:
// the previous list was built out of mailboxes that already existed, so an extension without one
// had no control anywhere in the product.
//
// WHAT STAYS WITH THE CUSTOMER. Whether Verso or the PBX owns voicemail is one decision for
// the account — flipping it per line would mean two pipelines emailing the same customer.
// It is shown here as context, read-only, with a link. Who gets notified is per mailbox and
// is edited on the box itself.

import { useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useVoicemailStatus } from '../../hooks/useVoicemailStatus.js';
import { VoicemailBox, VoicemailModeCard, MailboxCloudSettings } from '../shared/VoicemailPanel.jsx';
import FeatureSwitch from '../shared/FeatureSwitch.jsx';
import MailboxGreetings from './MailboxGreetings.jsx';

export default function LineVoicemail({ line, canWrite }) {
  const queryClient = useQueryClient();
  const extQ = useQuery({
    queryKey: ['line', String(line.id), 'extensions'],
    queryFn: () => api.get(`/lines/${line.id}/extensions`),
  });

  // The customer's voicemail payload, filtered to this line below. One fetch shared with
  // every other screen that reads it — a second endpoint scoped to the line would be a
  // second answer to "does this mailbox exist", and those drift.
  const vmQ = useQuery({
    queryKey: ['subscriber', String(line.subscriberId), 'voicemails'],
    queryFn: () => api.get(`/subscribers/${line.subscriberId}/voicemails`),
    enabled: Boolean(line.subscriberId),
    refetchInterval: 60_000,
  });

  const serversQ = useQuery({
    queryKey: ['sip-servers'],
    queryFn: () => api.get('/sip-servers'),
    staleTime: 5 * 60_000,
  });

  const { entryFor: vmEntryFor } = useVoicemailStatus();

  const agentOf = useMemo(() => {
    const list = Array.isArray(serversQ.data) ? serversQ.data
      : (serversQ.data?.sip_servers || serversQ.data?.servers || []);
    const map = {};
    for (const s of list) map[s.id] = s.agentId || s.agent_id;
    return map;
  }, [serversQ.data]);

  // The extensions this line rings, in ring order. The FIRST is the primary — the same
  // rule the ring group is numbered from, so the two screens cannot disagree about which
  // extension is "first".
  // TURNING VOICEMAIL ON AND OFF FOR THE LINE.
  //
  // Kevin, 2026-08-19: "there is no way to turn it off for a line?... there should be a slider
  // for turning voicemail on/off for the line while also showing the current state."
  //
  // There genuinely was not. The only voicemail controls were the cloud/PBX mode switch — which
  // refuses without a contact email — and the build, which switches voicemail ON whenever the
  // package includes it. So a customer who should not have a mailbox could not be given that
  // answer anywhere, and the next rebuild would have re-created it: the planner skips
  // `enable-extension-voicemail` only when somebody has explicitly said off
  // (`settings.voicemail.enabled === false`), and nothing could write that. Art Caruso's line
  // gained a mailbox this way on 2026-08-19.
  //
  // The route already existed and already does the whole job — it writes the flag AND pushes the
  // change to FreePBX. What was missing was a control, so this is a switch and not a new API.
  //
  // IT REPLACES THE BADGE rather than sitting beside it. Two things that both claim to say
  // whether voicemail is on is how they end up disagreeing.
  const toggle = useMutation({
    mutationFn: ({ accountId, enabled }) => api.put(`/sip-accounts/${accountId}/voicemail-settings`, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['line', String(line.id)] });
      queryClient.invalidateQueries({ queryKey: ['subscriber', String(line.subscriberId), 'voicemails'] });
    },
  });

  const rings = extQ.data?.extensions || [];
  // A line with nothing written down still rings its own account (the fallback in
  // lineDevices.js). Without this the ordinary single-extension line shows no mailbox at
  // all — the majority of the fleet.
  const ringIds = rings.length
    ? rings.map((e) => e.sipAccountId)
    : (line.sipAccountId ? [line.sipAccountId] : []);
  const primaryId = ringIds[0] ?? null;

  const allBoxes = vmQ.data?.boxes || [];
  const boxes = ringIds
    .map((accountId) => allBoxes.find((b) => b.sip_account_id === accountId))
    .filter(Boolean);
  const mode = vmQ.data?.voicemail_mode || 'freepbx';

  if (extQ.isLoading || vmQ.isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
        Loading voicemail…
      </div>
    );
  }

  // Every extension this line rings other than the primary. Was "drift, and only drift" — the
  // ones that had a mailbox the line did not use — which is the wrong frame now that a
  // department mailbox is deliberate (see the header).
  // EVERY non-primary extension this line rings, whether it has a mailbox or not. The old
  // derivation kept only the ones that DID, which is why a department extension had no switch:
  // you cannot turn a mailbox on from a list built out of mailboxes that already exist.
  const otherExtensions = rings
    .filter((e) => e.sipAccountId !== primaryId)
    .map((e) => ({
      accountId: e.sipAccountId,
      extension: e.username || allBoxes.find((b) => b.sip_account_id === e.sipAccountId)?.extension
        || String(e.sipAccountId),
      // The LIVE state from the host, not the intent — same rule as the line's own badge above.
      // FeatureSwitch takes the WORD, not a boolean (`checked = state === 'on'`), so a boolean
      // here renders an unchecked switch for a mailbox that exists.
      // An unreachable host reads 'off' rather than claiming a mailbox exists.
      on: vmEntryFor(e.sipAccountId)?.enabled === true ? 'on' : 'off',
    }));

  return (
    <div className="space-y-5">
      {/* ON OR OFF, AND WHERE MESSAGES LAND. Two lines, both about what the caller and the
          customer experience.

          WHAT WENT: a paragraph naming the primary extension and explaining that FreePBX has
          no mailbox for a ring group so one extension's box serves the line; a second card
          listing the other four extensions with a paragraph on why none of them has a mailbox;
          and a four-line privacy notice. All of it true, none of it anything an operator does
          something about — and it pushed the messages themselves below the fold. Kevin,
          2026-08-13: "hide the details from the operator... so the operator doesn't get bogged
          down with confusing details." */}
      {/* SOLD, AND ACTUALLY WORKING, ARE TWO DIFFERENT FACTS.
          This badge read `line.voicemailEnabled` — the PACKAGE — and printed
          "On · callers can leave a message" for Art Caruso's number while PBX-01
          answered `novm` for that extension. The entitlement was being rendered as the
          behaviour, so the one screen an operator opens to answer "can this customer
          take messages?" said yes when the honest answer was no (Kevin, 2026-08-17).
          The pending banner had it right all along; this card contradicted it.

          `line.mailboxEnabled` is the live read from the host, tri-state — and the
          `null` matters: an unreachable host must not render as "Off", which would be
          the same lie pointing the other way. */}
      {/* ONE CARD: what callers get, and every setting that decides it.
          Kevin, 2026-08-17: "consolidate the two top cards... the voicemail status should be
          per line, so the settings should be in the top, not on each extension... this tab is
          not very operator friendly."

          WHAT WAS HERE: a card carrying a badge and one sentence, then a SECOND card headed
          "Voicemail management" opening with a red line about account scope and a four-clause
          paragraph about transcription, defaults, per-extension overrides and double-sending —
          and then the settings themselves hidden behind a "Cloud settings" disclosure on the
          MAILBOX row, which is a per-extension place for a per-line decision. Four things to
          read and two places to click before you learn whether the customer can take a message.

          A LINE HAS ONE MAILBOX. On a ring group FreePBX gives the mailbox to the primary
          extension — the build does that, and the no-answer destination points there. That is
          stated as a fact where it is relevant instead of being explained as a caveat. */}
      {(() => {
        const sold = line.voicemailEnabled !== false;
        const onPbx = line.mailboxEnabled;          // true | false | null (not asked)
        const state = !sold ? 'off' : onPbx === false ? 'not-on-pbx' : onPbx === true ? 'on' : 'unknown';
        // THE SWITCH'S STATE, which is not quite this card's state.
        //
        // `not-on-pbx` — sold, no mailbox on the host — is OFF as far as a caller is concerned,
        // and the switch says so. Turning it on creates the mailbox, which is exactly what an
        // operator means by the gesture.
        //
        // `locked` when the PACKAGE does not include voicemail: off, greyed, unchangeable, with
        // the reason. Kevin, 2026-08-19: "if the feature is disabled because of the service
        // package, it should be off and grayed out so that it cannot be changed."
        const packageExcludes = line.voicemailInPackage === false;
        const swState = packageExcludes ? 'locked'
          : state === 'unknown' ? 'unknown'
          : state === 'on' ? 'on' : 'off';
        const swReason = packageExcludes
          ? `${line.packageName || 'This package'} does not include voicemail.`
          : swState === 'unknown'
            ? 'The phone system could not be reached, so this cannot be changed right now.'
            : null;
        const mailboxAccountId = primaryId;
        const primaryMessages = boxes.find((b) => b.sip_account_id === primaryId)?.messages;
        const says = {
          off:          'Callers who are not answered ring out. Nobody can leave a message.',
          'not-on-pbx': 'Sold with voicemail, but the phone system has no mailbox for it. '
                        + 'Callers ring out. Building the line switches it on.',
          unknown:      'Sold with voicemail. The phone system could not be reached to confirm '
                        + 'the mailbox is really there.',
          on:           boxes.length === 0
            ? 'This line has no extension yet, so there is nowhere to leave a message.'
            : 'Callers who are not answered can leave a message.',
        }[state];
        const primaryBox = boxes.find((b) => b.sip_account_id === primaryId);

        return (
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 className="text-sm font-semibold text-gray-900">Voicemail</h2>
              <div className="flex items-center gap-3">
              {/* WHERE IT IS HANDLED, BESIDE WHETHER IT IS ON. Kevin, 2026-08-19: "move the badge
                  and button for switching voicemail to the cloud to be next to the on/off toggle."
                  Two decisions about one feature — is it on, and who handles it — read as one
                  thing when they are in one place. The paragraph explaining the choice, and that
                  it is account-wide, stays below with its controls suppressed. */}
              {line.subscriberId && vmQ.data && (
                <VoicemailModeCard
                  compact
                  subscriberId={line.subscriberId}
                  mode={mode}
                  recipients={vmQ.data.recipients}
                  canWrite={canWrite}
                  cloudAllowed={line.voicemailInPackage !== false}
                  tierName={line.packageName}
                  onChanged={() => queryClient.invalidateQueries({
                    queryKey: ['subscriber', String(line.subscriberId), 'voicemails'],
                  })}
                />
              )}
              <FeatureSwitch
                state={swState}
                label="Voicemail for this line"
                canWrite={canWrite && mailboxAccountId != null}
                busy={toggle.isPending}
                reason={swReason}
                onChange={(next) => toggle.mutate({ accountId: mailboxAccountId, enabled: next })}
                // TURNING IT OFF IS DESTRUCTIVE ON THE PBX. FreePBX's disableVoiceMail takes the
                // mailbox and its stored messages with it. Under cloud voicemail Verso still holds
                // them (R2/D1), so the warning has to say which of those two situations this is
                // rather than one generic scare.
                confirm={{
                  title: 'Turn voicemail off for this line?',
                  confirmLabel: 'Turn voicemail off',
                  body: (
                    <>
                      <p>
                        Callers who are not answered will ring out, and the mailbox on the phone
                        system is removed.
                      </p>
                      <p>
                        {mode === 'cloud'
                          ? 'This account is on cloud voicemail, so Verso keeps its copy of any '
                            + 'existing messages — they stay readable here.'
                          : primaryMessages
                            ? `This account's voicemail is managed on the phone system, so the `
                              + `${primaryMessages} stored message${primaryMessages === 1 ? '' : 's'} `
                              + 'will be deleted with it. There is no other copy.'
                            : 'This account\u2019s voicemail is managed on the phone system. There are '
                              + 'no stored messages, so nothing is lost.'}
                      </p>
                    </>
                  ),
                }}
              />
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-600">{says}</p>

            {/* THE SETTING THE SWITCH CANNOT REACH.
                Kevin, 2026-08-20, on Christopher Sloan: "doesn't have voicemail set up in the
                other system, so voicemail is off." The line is sold with voicemail and the host
                has no mailbox, so the switch already reads OFF — the caller's experience — and the
                only gesture it offers is turning it ON. There was no way to say "and do not add
                one", which matters because the sentence above is literal: the next build creates
                the mailbox, switching voicemail on for a customer who does not use it.

                Deliberately NOT solved by making the switch show the entitlement instead. That is
                exactly the lie removed on 2026-08-17, when this card printed "On · callers can
                leave a message" over a host answering `novm`. The switch keeps saying what callers
                get; the intent gets its own words. */}
            {state === 'not-on-pbx' && canWrite && mailboxAccountId != null && (
              <button
                type="button"
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ accountId: mailboxAccountId, enabled: false })}
                className="mt-1 text-xs font-medium text-blue-700 underline hover:text-blue-900 disabled:opacity-50"
              >
                {toggle.isPending ? 'Saving…' : 'Don’t add one — this customer does not use voicemail'}
              </button>
            )}

            {/* Only when there is more than one extension, because only then is "which one
                holds the mailbox" a question anybody has. */}
            {state === 'on' && primaryBox && rings.length > 1 && (
              <p className="mt-1 text-xs text-gray-500">
                Messages go to <span className="font-mono">{primaryBox.extension}</span>, the
                first extension this number rings.
              </p>
            )}

            {/* THE SETTINGS, in the card that states the status they produce. */}
            {primaryBox && mode === 'cloud' && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <MailboxCloudSettings
                  box={primaryBox}
                  subscriberId={line.subscriberId}
                  canWrite={canWrite}
                  pbxEnabled={vmEntryFor(primaryBox.sip_account_id)?.enabled ?? null}
                  onSaved={() => queryClient.invalidateQueries({
                    queryKey: ['subscriber', String(line.subscriberId), 'voicemails'],
                  })}
                />
              </div>
            )}

            {/* THE "VOICEMAIL MANAGEMENT" BLOCK IS GONE (2026-08-19).
                Kevin: "this copy can be deleted: 'Voicemail management / Applies to every line on
                this account. / Handled by Verso Cloud. Messages are emailed to everyone on the
                account with voicemail email on: …'"

                Every clause in it is now shown as a control instead of prose: which system handles
                voicemail is the badge and button beside the on/off switch, and who is emailed is the
                roster of people with a slider each. Listing the addresses in a sentence was a third
                statement of the same fact, and the account-wide caveat mattered only because the
                CHOICE was buried down here — it is in the header now, where the switch is. */}

          </div>
        );
      })()}

      {/* One line, not four. */}
      <p className="text-xs text-gray-500">
        🔒 Messages are private to the subscriber and cannot be played from here. Use portal
        view-as (audited) to help with one.
      </p>

      {/* THE LINE'S MAILBOX — the primary's, which is where the ring group's no-answer
          destination points and the only one the build switches on.

          MESSAGES ONLY. The settings disclosure that used to hang off this row moved into
          the card above, next to the status it produces: it was a per-extension control for
          a per-line decision, and it put two clicks between an operator and "can this
          customer take a message?". */}
      {boxes.filter((b) => b.sip_account_id === primaryId).map((box) => (
        <VoicemailBox
          key={box.sip_account_id}
          box={box}
          subscriberId={line.subscriberId}
          agentId={agentOf[box.sip_server_id] || null}
          pbxEnabled={vmEntryFor(box.sip_account_id)?.enabled ?? null}
          canWrite={canWrite}
          showConfigControls={false}
          cloudMode={false}
        />
      ))}

      {/* WHAT CALLERS HEAR WHEN A MAILBOX PICKS UP.
          Sits directly under the messages and above the department switches, because it answers the
          question the switches create: turning a mailbox on for a menu key is half the job, and
          before this card the other half could only be done from the customer's own portal. Kevin,
          2026-09-02: "that is working now. the voicemail greeting is missing." */}
      {line.subscriberId && (
        <MailboxGreetings
          line={line}
          canWrite={canWrite}
          // The live per-extension read the switches above already use. Passed down so the greetings
          // card does not offer a Set button for an extension with no mailbox — which is what
          // 17208934175 did on the first live attempt, refusing only after a recording was chosen.
          voicemailEnabled={Object.fromEntries(
            ringIds.map((id) => [id, vmEntryFor(id)?.enabled ?? null]),
          )}
        />
      )}

      {/* THE OTHER EXTENSIONS, WITH A SWITCH EACH.
          This was a paragraph saying any mailbox other than the primary's was collecting
          messages "nobody checks" — correct while the mailbox belonged to the primary, and
          wrong the moment a menu made departments real. Lyons Communications' attendant sends
          key 2 to Sales, 3 to Technical Support, 4 to Customer Service, and each of those
          prompts ends "to leave a voicemail, press 1", so those mailboxes are the point rather
          than an accident (Kevin, 2026-08-27).
          The old copy also left an operator nothing to do: it named the problem and offered no
          control. And a mailbox cannot be turned ON from a list that only shows mailboxes that
          already exist — which is why the three extensions built for those departments had no
          switch anywhere in the product. A row per extension, on or off, is both the report and
          the control. */}
      {otherExtensions.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-gray-900">Other extensions on this line</h3>
          <p className="mt-1 text-sm text-gray-600">
            Give one its own mailbox when callers are sent to it directly — a menu key for
            sales or support, for instance.
          </p>
          <ul className="mt-3 divide-y divide-gray-100">
            {otherExtensions.map((x) => (
              <li key={x.accountId} className="flex items-center justify-between gap-4 py-2">
                <span className="font-mono text-sm text-gray-900">{x.extension}</span>
                <FeatureSwitch
                  state={x.on}
                  label={`Voicemail for ${x.extension}`}
                  canWrite={canWrite}
                  busy={toggle.isPending}
                  onChange={(next) => toggle.mutate({ accountId: x.accountId, enabled: next })}
                  confirm={x.on === 'on' ? {
                    title: `Turn voicemail off for ${x.extension}?`,
                    confirmLabel: 'Turn voicemail off',
                    body: (
                      <p>
                        Callers sent to this extension will ring out, and the mailbox on the
                        phone system is removed along with anything in it.
                      </p>
                    ),
                  } : undefined}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
